"use client";
import React, { useCallback } from "react";

import { Swiper, SwiperSlide } from "swiper/react";
import gsap from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";

import { LinkElement } from "../link/link";
import { ListItem, ServiceList, TextListBlock } from "../../../sanity/lib/types/textListBlock";

import useIsMobile from "../../hooks/useIsMobile";
import { useGSAP } from "@gsap/react";

gsap.registerPlugin(ScrollTrigger);
gsap.registerPlugin(useGSAP);

export const TextListSection = ({ textSection }: { textSection: TextListBlock }) => {
    const { isMobile } = useIsMobile();

    useGSAP(() => {
        const scrollTriggerSettings = {
            trigger: ".text-list-section",
            start: "top 70%",
            toggleActions: "play none none none",
        };

        gsap.set(".gsap-headline", { y: 50, opacity: 0 });

        gsap.to(".gsap-headline", {
            y: 0,
            opacity: 1,
            scrollTrigger: scrollTriggerSettings,
            stagger: 0.1,
        });
    }, [{ dependencies: [isMobile, textSection] }]);

    const listItems =
        textSection.listType === "services" ? textSection.services : textSection.listItems;

    const getPosition = useCallback((index: number, totalItems: number) => {
        const itemsPerRow = 3;

        const row = Math.floor(index / itemsPerRow);
        const positionInRow = index % itemsPerRow;

        const isLastRow = row === Math.floor((totalItems - 1) / itemsPerRow);
        const itemsInLastRow = totalItems % itemsPerRow || itemsPerRow;

        const itemsInCurrentRow = isLastRow ? itemsInLastRow : itemsPerRow;

        const colFromRight = itemsPerRow - (itemsInCurrentRow - 1 - positionInRow);
        const absoluteCol = row * itemsPerRow + colFromRight;

        return absoluteCol;
    }, []);

    return (
        <section className="container py-20 text-list-section">
            <div className="grid grid-cols-2 gap-5 mx-2.5 md:mx-5">
                <h2 className="col-span-2 whitespace-pre-line gsap-headline m-headline-1 md:d-headline-2 md:col-span-1">
                    {textSection.title}
                </h2>
                <p className="flex items-end col-span-2 gsap-headline m-body-l md:d-body-l md:col-span-1">
                    {textSection.text}
                </p>
            </div>

            {textSection.link && (
                <div className="gsap-headline flex justify-start my-11 mx-2.5 md:mx-5">
                    <LinkElement className="link" link={textSection.link} />
                </div>
            )}

            <div className="w-full h-px bg-black"></div>
            <div className="container">
                {isMobile && (
                    <div className="mx-2.5 mt-8 mb-3.5">
                        <Swiper slidesPerView={1.7} spaceBetween={20}>
                            {listItems?.map((listItem, index) => (
                                <SwiperSlide key={index} className="swiper-slide">
                                    <div className="col-span-1" key={listItem.title}>
                                        <p className="mb-6 m-title-highlight">{listItem.title}</p>
                                        <SubList type={textSection.listType} listItems={listItem} />
                                    </div>
                                </SwiperSlide>
                            ))}
                        </Swiper>
                    </div>
                )}
                {!isMobile && (
                    <div className="flex flex-col mt-8 mx-2.5 md:mx-5 gap-11">
                        <div className="grid grid-cols-4 gap-x-5 grid-area-text-list">
                            {listItems &&
                                listItems.map((listItem, index) => (
                                    <React.Fragment key={index + "listitem"}>
                                        <div
                                            className={`pb-6 grid-area-text-list-item-${getPosition(index, listItems.length)}`}
                                        >
                                            <h3 className="mb-6 m-title-highlight md:d-title-highlight">
                                                {textSection.listType === "services" && (
                                                    <span>0{++index} </span>
                                                )}
                                                {listItem.title}
                                            </h3>
                                            <SubList
                                                type={textSection.listType}
                                                listItems={listItem}
                                            />
                                        </div>
                                    </React.Fragment>
                                ))}
                        </div>
                    </div>
                )}
            </div>
        </section>
    );
};

const SubList = ({
    type,
    listItems,
}: {
    type: "services" | "custom";
    listItems: ServiceList | ListItem;
}) => {
    if (type === "services") {
        return (listItems as ServiceList).serviceCategories
            .filter((cat) => cat.highlight)
            .map((subitem, index) => (
                <div key={index} className="overflow-hidden mb-0.5">
                    <p className="m-body-m md:d-body-m text-list-section-subitem">
                        {subitem.title}
                    </p>
                </div>
            ));
    }

    return (listItems as ListItem).listItems.map((subitem, index) => (
        <p key={index} className="m-body-m md:d-body-m mb-0.5">
            {subitem}
        </p>
    ));
};
